import React, { useEffect, useRef, useCallback, useState, forwardRef } from 'react';
import type { ReactNode } from 'react';

interface ShadowDOMContentRendererProps {
  // HTML content to render (EPUB chapter HTML)
  htmlContent: string;

  // CSS content to inject (EPUB stylesheets)
  cssContent?: string;

  // Additional stylesheets URLs to load
  stylesheetUrls?: string[];

  // Callback when links are clicked
  onLinkClick?: (href: string, event: Event) => void;

  // Callback when text is selected
  onTextSelection?: (selectedText: string, range: Range, textContext?: string, targetOffset?: number) => void;

  // Callback when Shadow DOM is ready (ref has been set)
  onReady?: () => void;

  // Enable/disable text selection
  allowSelection?: boolean;

  // Class name for the host element
  className?: string;

  // Children to render in light DOM (for fallback)
  children?: ReactNode;

  // Theme mode for reader controls
  theme?: 'light' | 'dark';

  // Reader settings (user preferences)
  fontSize?: number;
  fontFamily?: string;
  lineHeight?: number;
  backgroundColor?: string;
  textColor?: string;
  useBookStyles?: boolean; // If true, use original book styles; if false, apply custom settings

  // Column count for spread-mode layout (1 = single column, 2 = two-page spread).
  // Applied INSIDE the shadow root via .content-container { column-count: N }
  // because CSS columns on the host cannot flow across shadow boundaries.
  columnCount?: number;
}

export const ShadowDOMContentRenderer = forwardRef<HTMLDivElement, ShadowDOMContentRendererProps>(({
  htmlContent,
  cssContent,
  stylesheetUrls = [],
  onLinkClick,
  onTextSelection,
  onReady,
  allowSelection = true,
  className,
  children,
  theme = 'light',
  fontSize = 16,
  fontFamily = 'Georgia, serif',
  lineHeight = 1.5,
  backgroundColor = '#FFFFFF',
  textColor = '#000000',
  useBookStyles = true,
  columnCount = 1
}, ref) => {
  const hostRef = useRef<HTMLDivElement>(null);
  const shadowRootRef = useRef<ShadowRoot | null>(null);
  const [isReady, setIsReady] = useState(false);

  // Store container ref for selection handling
  const containerRef = useRef<HTMLElement | null>(null);

  /**
   * Determine if a color is dark (to decide image backgrounds)
   * Returns true if the color is dark (needs white image backgrounds)
   */
  const isColorDark = useCallback((color: string): boolean => {
    // Convert hex to RGB
    let r = 0, g = 0, b = 0;

    if (color.startsWith('#')) {
      const hex = color.replace('#', '');
      if (hex.length === 6) {
        r = parseInt(hex.substring(0, 2), 16);
        g = parseInt(hex.substring(2, 4), 16);
        b = parseInt(hex.substring(4, 6), 16);
      }
    } else if (color.startsWith('rgb')) {
      const match = color.match(/\d+/g);
      if (match && match.length >= 3) {
        r = parseInt(match[0]);
        g = parseInt(match[1]);
        b = parseInt(match[2]);
      }
    }

    // Calculate relative luminance (WCAG formula)
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

    // If luminance < 0.5, it's a dark color
    return luminance < 0.5;
  }, []);

  // Attach event handlers for interactivity (defined before use)
  const attachEventHandlers = useCallback((container: HTMLElement) => {
    containerRef.current = container;

    // Handle link clicks
    if (onLinkClick) {
      container.addEventListener('click', (e) => {
        const target = e.target as HTMLElement;
        const link = target.closest('a');
        if (link && link.href) {
          e.preventDefault();
          onLinkClick(link.href, e);
        }
      });
    }
  }, [onLinkClick]);

  // Note: mouseup handler is now attached directly in the Shadow DOM content useEffect
  // This ensures it's attached whenever the content is rendered, avoiding timing issues

  // Initialize Shadow DOM (runs once on mount)
  useEffect(() => {
    if (!hostRef.current) return;

    // Create shadow root if it doesn't exist.
    // delegatesFocus is intentionally NOT set: with it enabled, clicking anywhere
    // inside the host auto-delegates focus to the first focusable descendant
    // (e.g. a TOC <a> anchor from the EPUB content) and the browser scrolls that
    // element into view, causing a "random jump" on first click.
    if (!shadowRootRef.current) {
      shadowRootRef.current = hostRef.current.attachShadow({
        mode: 'closed',
      });
    }

    setIsReady(true);
  }, []); // Empty dependency array - runs once on mount

  // Update forwarded ref whenever ref prop changes OR when component mounts
  // This ensures parent always gets the latest hostRef.current
  useEffect(() => {
    if (ref && hostRef.current) {
      if (typeof ref === 'function') {
        ref(hostRef.current);
      } else {
        ref.current = hostRef.current;
      }

      // Notify parent that Shadow DOM is ready
      onReady?.();
    }
  }, [ref, isReady, onReady]); // Depend on isReady to ensure hostRef.current is set

  // Update shadow DOM content
  useEffect(() => {
    if (!shadowRootRef.current || !isReady) return;

    const shadowRoot = shadowRootRef.current;

    // Clear existing content
    shadowRoot.innerHTML = '';

    // Create styles container
    const styleContainer = document.createElement('div');
    styleContainer.id = 'epub-styles';

    // Add base isolation styles with CSS layers
    const isolationStyles = document.createElement('style');
    isolationStyles.textContent = `
      /* Define cascade layers in order of priority (lowest to highest) */
      @layer reset, epub, reader-controls, critical;

      /* Reset layer - normalize shadow DOM styles */
      @layer reset {
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        :host {
          display: block;
          width: 100%;
          height: 100%;
          overflow: auto;
          position: relative;
        }
      }

      /* EPUB content styles will be inserted in epub layer */
      @layer epub {
        /* EPUB styles inserted here */
      }

      /* Reader control styles */
      @layer reader-controls {
        /* Content container - proper sizing and spacing */
        .content-container {
          width: 100%; /* Use full width */
          padding: 2rem; /* 32px - equivalent to p-8 */
          line-height: 1.75; /* prose line-height */
          font-size: 1.125rem; /* 18px - prose-lg */
          color: var(--text-color, #1f2937);
          column-count: var(--reader-column-count, 1);
          column-gap: 48px;
          column-rule: var(--reader-column-rule, none);
        }

        /* Prose styling for better readability */
        .content-container p {
          margin-bottom: 1.25em;
        }

        .content-container h1,
        .content-container h2,
        .content-container h3,
        .content-container h4,
        .content-container h5,
        .content-container h6 {
          margin-top: 1.5em;
          margin-bottom: 0.5em;
          font-weight: 600;
          line-height: 1.25;
        }

        .content-container h1 {
          font-size: 2.25em;
        }

        .content-container h2 {
          font-size: 1.875em;
        }

        .content-container h3 {
          font-size: 1.5em;
        }

        .content-container ul,
        .content-container ol {
          margin-top: 1em;
          margin-bottom: 1em;
          padding-left: 1.625em;
        }

        .content-container li {
          margin-top: 0.5em;
          margin-bottom: 0.5em;
        }

        .content-container img {
          max-width: 100%;
          height: auto;
          margin: 1.5em auto;
          display: block;
        }

        .content-container blockquote {
          margin: 1.5em 0;
          padding-left: 1em;
          border-left: 4px solid #e5e7eb;
          font-style: italic;
        }

        .content-container code {
          background-color: #f3f4f6;
          padding: 0.2em 0.4em;
          border-radius: 3px;
          font-size: 0.875em;
          font-family: monospace;
        }

        .content-container pre {
          background-color: #f3f4f6;
          padding: 1em;
          border-radius: 0.375rem;
          overflow-x: auto;
          margin: 1.5em 0;
        }

        .content-container pre code {
          background-color: transparent;
          padding: 0;
        }

        .content-container table {
          width: 100%;
          margin: 1.5em 0;
          border-collapse: collapse;
        }

        .content-container th,
        .content-container td {
          padding: 0.75em;
          border: 1px solid #e5e7eb;
          text-align: left;
        }

        .content-container th {
          background-color: #f9fafb;
          font-weight: 600;
        }

        .highlight {
          background-color: yellow !important;
          cursor: pointer;
        }

        /* Shadow DOM highlights - preserve inline background-color */
        .shadow-dom-range-highlight {
          cursor: pointer;
          /* background-color is set via inline style */
        }

        .annotation {
          position: relative;
          border-bottom: 2px dotted #4a90e2;
        }

        .bookmark-indicator {
          position: absolute;
          right: -20px;
          top: 0;
          width: 16px;
          height: 20px;
          background: #ff4444;
          clip-path: polygon(0 0, 100% 0, 100% 70%, 50% 100%, 0 70%);
        }

        ::selection {
          background-color: ${theme === 'dark' ? 'rgb(var(--color-primary-500) / 0.5)' : 'rgb(var(--color-primary-500) / 0.3)'};
        }

        ${!allowSelection ? `
          .content-container {
            user-select: none;
            -webkit-user-select: none;
          }
        ` : ''}

        @media (pointer: coarse) {
          .content-container {
            -webkit-touch-callout: none !important;
            -webkit-tap-highlight-color: transparent;
          }
        }

        /* Mobile-specific padding adjustments */
        @media (max-width: 767px) {
          .content-container {
            padding: 1rem; /* 16px sides - less cramped on mobile */
            padding-bottom: 8rem; /* 128px - account for toolbar (56px) + bottom nav (64px) + extra space (8px) */
          }
        }
      }

      /* Critical overrides - highest priority */
      @layer critical {
        /* CSS Custom Properties for reader settings */
        :host {
          --reader-font-family: ${fontFamily};
          --reader-font-size: ${fontSize}px;
          --reader-line-height: ${lineHeight};
          --reader-text-color: ${textColor};
          --reader-bg-color: ${backgroundColor};
          --reader-img-bg: ${isColorDark(backgroundColor) ? '#FFFFFF' : 'transparent'};
          --reader-img-padding: ${isColorDark(backgroundColor) ? '8px' : '0'};
          --reader-img-radius: ${isColorDark(backgroundColor) ? '4px' : '0'};
        }

        ${!useBookStyles ? `
        /* User preference overrides - these override ALL EPUB styles */
        /* Apply to content container */
        .content-container {
          font-family: var(--reader-font-family) !important;
          font-size: var(--reader-font-size) !important;
          line-height: var(--reader-line-height) !important;
          color: var(--reader-text-color) !important;
          background-color: var(--reader-bg-color) !important;
        }

        /* Apply to all children EXCEPT highlights and annotations */
        /* NOTE: Do NOT set background-color here - let highlights preserve their inline styles */
        .content-container *:not(.shadow-dom-range-highlight):not(.highlight):not(.annotation):not(.reader-highlight) {
          font-family: inherit !important;
          font-size: inherit !important;
          line-height: inherit !important;
          color: inherit !important;
        }

        /* Image backgrounds: white on dark backgrounds, transparent on light backgrounds */
        .content-container img {
          background-color: var(--reader-img-bg) !important;
          padding: var(--reader-img-padding) !important;
          border-radius: var(--reader-img-radius) !important;
        }

        /* Preserve heading size hierarchy (relative to base font size) */
        .content-container h1 {
          font-size: calc(var(--reader-font-size) * 2.25 / ${fontSize}) !important;
        }

        .content-container h2 {
          font-size: calc(var(--reader-font-size) * 1.875 / ${fontSize}) !important;
        }

        .content-container h3 {
          font-size: calc(var(--reader-font-size) * 1.5 / ${fontSize}) !important;
        }

        .content-container h4 {
          font-size: calc(var(--reader-font-size) * 1.25 / ${fontSize}) !important;
        }

        .content-container h5 {
          font-size: calc(var(--reader-font-size) * 1.125 / ${fontSize}) !important;
        }

        .content-container h6 {
          font-size: var(--reader-font-size) !important;
        }

        /* Smaller text for code blocks */
        .content-container code,
        .content-container pre code {
          font-size: calc(var(--reader-font-size) * 0.875 / ${fontSize}) !important;
        }
        ` : ''}

        /* CRITICAL: Ensure highlights preserve their inline background-color styles */
        /* Must be in critical layer to override container background */
        .content-container .shadow-dom-range-highlight,
        .content-container .highlight,
        .content-container .reader-highlight {
          /* Reset background to allow inline style to take effect */
          background-color: initial;
        }
      }
    `;
    shadowRoot.appendChild(isolationStyles);

    // Add EPUB CSS in the epub layer
    if (cssContent) {
      const epubStyles = document.createElement('style');
      epubStyles.textContent = `
        @layer epub {
          ${cssContent}
        }
      `;
      shadowRoot.appendChild(epubStyles);
    }

    // Create content container
    const contentContainer = document.createElement('div');
    contentContainer.className = 'content-container';
    contentContainer.innerHTML = htmlContent;

    // Track stylesheet loading to prevent FOUC (Flash of Unstyled Content)
    let loadedStylesheets = 0;
    const totalStylesheets = stylesheetUrls.length;

    // Hide content until stylesheets are loaded (prevents FOUC)
    if (totalStylesheets > 0) {
      contentContainer.style.opacity = '0';
      contentContainer.style.transition = 'opacity 0.15s ease-in';
    }

    // Load external stylesheets
    const stylesheetLoadPromises = stylesheetUrls.map(url => {
      return new Promise<void>((resolve, reject) => {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = url;
        link.onload = () => {
          loadedStylesheets++;
          console.log(`✅ Loaded stylesheet (${loadedStylesheets}/${totalStylesheets}): ${url}`);

          // When all stylesheets are loaded, show content
          if (loadedStylesheets === totalStylesheets) {
            contentContainer.style.opacity = '1';
            console.log(`🎨 All stylesheets loaded, content visible`);
          }
          resolve();
        };
        link.onerror = () => {
          console.error(`❌ Failed to load stylesheet: ${url}`);
          loadedStylesheets++;
          // Still show content even if stylesheet fails to load
          if (loadedStylesheets === totalStylesheets) {
            contentContainer.style.opacity = '1';
          }
          reject(new Error(`Failed to load stylesheet: ${url}`));
        };
        shadowRoot.appendChild(link);
      });
    });

    // Attach event handlers
    attachEventHandlers(contentContainer);

    // Attach mouseup handler for text selection (if enabled)
    let cleanupSelection: (() => void) | null = null;

    if (onTextSelection && allowSelection) {
      let lastSelection: { text: string; range: Range; textContext?: string; targetOffset?: number } | null = null;

      // Track selection changes to capture the selection before it's cleared.
      // Prefer shadowRoot.getSelection() (Chromium) so anchorNode/focusNode land on
      // text nodes INSIDE the closed shadow root. document.getSelection() from the
      // outer document returns a degraded range collapsed at the shadow host, which
      // breaks both offset math and getBoundingClientRect() for menu positioning.
      const getShadowAwareSelection = (): Selection | null => {
        const sr = shadowRoot as ShadowRoot & { getSelection?: () => Selection | null };
        if (typeof sr.getSelection === 'function') {
          const s = sr.getSelection();
          if (s && s.rangeCount > 0 && s.toString().trim()) return s;
        }
        return document.getSelection();
      };

      const handleSelectionChange = () => {
        const selection = getShadowAwareSelection();

        if (selection && selection.toString().trim() && selection.rangeCount > 0) {
          const selectedText = selection.toString();
          const range = selection.getRangeAt(0).cloneRange();

          // Extract surrounding text context to help identify the correct occurrence
          // Use the browser's Selection object to get text before the selection
          let textContext = '';
          try {
            const fullText = contentContainer.textContent || '';

            // Try to use the Selection's anchorNode to calculate position
            if (selection.anchorNode && selection.focusNode) {
              // Create a range from container start to the anchor point
              const rangeBeforeSelection = shadowRoot.ownerDocument.createRange();
              rangeBeforeSelection.selectNodeContents(contentContainer);

              try {
                rangeBeforeSelection.setEnd(selection.anchorNode, selection.anchorOffset);
                const textBeforeSelection = rangeBeforeSelection.toString();

                console.log('📍 Position info:', {
                  textBeforeLength: textBeforeSelection.length,
                  fullTextLength: fullText.length,
                  selectedText: selectedText.substring(0, 30),
                  anchorNode: selection.anchorNode.nodeName
                });

                const actualSelectionOffset = textBeforeSelection.length;

                // Extract context: 100 chars before + selection + 100 chars after
                const contextStart = Math.max(0, actualSelectionOffset - 100);
                const contextEnd = Math.min(fullText.length, actualSelectionOffset + selectedText.length + 100);
                textContext = fullText.substring(contextStart, contextEnd);

                console.log('📝 Captured text context:', {
                  selectedText: selectedText.substring(0, 50),
                  actualOffset: actualSelectionOffset,
                  context: textContext.substring(0, 100) + '...',
                  contextLength: textContext.length
                });
              } catch (rangeError) {
                console.warn('Range method failed, trying fallback:', rangeError);
                throw rangeError; // Re-throw to trigger the outer catch block
              }
            } else {
              throw new Error('No anchorNode or focusNode in selection');
            }
          } catch (e) {
            console.warn('Failed to extract text context using Selection, using visual search fallback:', e);

            // Ultimate fallback: Don't capture context at all
            // The later code will try to find the occurrence by searching ALL of them
            // and we'll pick the first one (which may be wrong, but it's better than nothing)
            textContext = '';
          }

          lastSelection = {
            text: selectedText,
            range: range,
            textContext: textContext
          };

          console.log('✅ Selection stored:', {
            text: lastSelection.text.substring(0, 50),
            startContainer: lastSelection.range.startContainer.nodeName,
            startOffset: lastSelection.range.startOffset,
            endContainer: lastSelection.range.endContainer.nodeName,
            endOffset: lastSelection.range.endOffset,
            hasContext: !!textContext
          });
        }
      };

      const handleMouseUp = (event: MouseEvent) => {
        // Verify the mouseup happened within our shadow DOM content
        const target = event.target as Node;
        let isInOurContent = false;

        let node: Node | null = target;
        while (node) {
          if (node === contentContainer) {
            isInOurContent = true;
            break;
          }
          node = node.parentNode;
        }

        console.log('Mouseup detected:', {
          target: (target as Element)?.tagName || target?.nodeName,
          isInOurContent,
          hasStoredSelection: !!lastSelection
        });

        // Use the last captured selection if mouseup was in our content
        if (lastSelection && isInOurContent) {
          console.log('✅ Using stored selection on mouseup');

          // IMPROVED: Use the mouseup target element to find the correct occurrence
          // This is more reliable than trying to use Selection/Range APIs in Shadow DOM
          const targetElement = target as Element;

          // Recalculate context based on mouseup target position
          try {
            const fullText = contentContainer.textContent || '';

            // Find the position of the target element in the text
            // Walk through text nodes and find the cumulative offset where the target is
            const walker = shadowRoot.ownerDocument.createTreeWalker(
              contentContainer,
              NodeFilter.SHOW_TEXT,
              null
            );

            let targetOffset = -1;
            let cumulativeOffset = 0;
            let textNode;

            while ((textNode = walker.nextNode())) {
              if (textNode.nodeType === Node.TEXT_NODE && textNode.textContent) {
                // Check if this text node is inside or near the mouseup target
                if (targetElement.contains(textNode) || textNode.parentElement === targetElement || textNode.parentElement?.parentElement === targetElement) {
                  targetOffset = cumulativeOffset;
                  console.log('🎯 Found target element at text offset:', targetOffset);
                  break;
                }
                cumulativeOffset += textNode.textContent.length;
              }
            }

            if (targetOffset !== -1) {
              // Extract context around the target position
              const contextStart = Math.max(0, targetOffset - 100);
              const contextEnd = Math.min(fullText.length, targetOffset + lastSelection.text.length + 100);
              const betterContext = fullText.substring(contextStart, contextEnd);

              console.log('📍 Recalculated context based on mouseup target:', {
                targetOffset,
                contextPreview: betterContext.substring(0, 100) + '...'
              });

              // Update the context and targetOffset
              lastSelection.textContext = betterContext;
              lastSelection.targetOffset = targetOffset;
            }
          } catch (e) {
            console.warn('Failed to recalculate context from mouseup target:', e);
          }

          onTextSelection(lastSelection.text, lastSelection.range, lastSelection.textContext, lastSelection.targetOffset);
          lastSelection = null; // Clear after use
        } else {
          console.log('❌ No stored selection available or mouseup outside content');
          lastSelection = null; // Clear anyway
        }
      };

      document.addEventListener('selectionchange', handleSelectionChange);
      contentContainer.addEventListener('mouseup', handleMouseUp);

      // Touch: iOS fires touchcancel (not mouseup) on long-press, so
      // mouseup never calls onTextSelection. Use a debounced
      // selectionchange to fire when a stable selection exists.
      let touchSelDebounce: number | null = null;
      const isTouchDev = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

      const handleTouchSel = () => {
        if (touchSelDebounce) window.clearTimeout(touchSelDebounce);
        touchSelDebounce = window.setTimeout(() => {
          if (!lastSelection || !lastSelection.text.trim()) return;
          onTextSelection(lastSelection.text, lastSelection.range, lastSelection.textContext, lastSelection.targetOffset);
          lastSelection = null;
        }, 300);
      };

      if (isTouchDev) {
        document.addEventListener('selectionchange', handleTouchSel);
      }

      contentContainer.addEventListener('contextmenu', function(e: Event) { e.preventDefault(); });

      cleanupSelection = () => {
        document.removeEventListener('selectionchange', handleSelectionChange);
        contentContainer.removeEventListener('mouseup', handleMouseUp);
        if (isTouchDev) document.removeEventListener('selectionchange', handleTouchSel);
        if (touchSelDebounce) window.clearTimeout(touchSelDebounce);
      };
    }

    shadowRoot.appendChild(contentContainer);

    // Notify parent that Shadow DOM content is ready
    // Wait for stylesheets to load before calling onReady (prevents FOUC)
    let frameId: number | null = null;

    if (totalStylesheets > 0) {
      // Wait for all stylesheets to load
      Promise.allSettled(stylesheetLoadPromises).then(() => {
        // Use requestAnimationFrame to ensure DOM is fully painted
        frameId = requestAnimationFrame(() => {
          onReady?.();
        });
      });
    } else {
      // No stylesheets to load, call onReady immediately
      frameId = requestAnimationFrame(() => {
        onReady?.();
      });
    }

    // Return cleanup function
    return () => {
      if (cleanupSelection) {
        cleanupSelection();
      }
      if (frameId !== null) {
        cancelAnimationFrame(frameId);
      }
    };
  }, [htmlContent, cssContent, stylesheetUrls, isReady, allowSelection, theme, isColorDark, onReady]);
  // Note: fontSize, fontFamily, lineHeight, backgroundColor, textColor are NOT in dependencies
  // to prevent Shadow DOM from rebuilding. They are applied via CSS custom properties below.
  // Note: attachEventHandlers and onTextSelection are NOT in dependencies to prevent Shadow DOM
  // from re-rendering when callbacks change. Event handlers capture current callback values
  // via closure when content is created, which is sufficient.

  // Update CSS custom properties when reader settings change (without rebuilding Shadow DOM)
  useEffect(() => {
    if (!hostRef.current) return;

    const host = hostRef.current;

    // Update CSS custom properties on the host element
    host.style.setProperty('--reader-font-family', fontFamily);
    host.style.setProperty('--reader-font-size', `${fontSize}px`);
    host.style.setProperty('--reader-line-height', String(lineHeight));
    host.style.setProperty('--reader-text-color', textColor);
    host.style.setProperty('--reader-bg-color', backgroundColor);

    // Update image-related properties based on background darkness
    const isDark = isColorDark(backgroundColor);
    host.style.setProperty('--reader-img-bg', isDark ? '#FFFFFF' : 'transparent');
    host.style.setProperty('--reader-img-padding', isDark ? '8px' : '0');
    host.style.setProperty('--reader-img-radius', isDark ? '4px' : '0');

    // Column count for spread mode — flows chapter content across two columns.
    host.style.setProperty('--reader-column-count', String(columnCount));
    host.style.setProperty(
      '--reader-column-rule',
      columnCount > 1 ? '1px solid rgba(0,0,0,0.08)' : 'none'
    );
  }, [fontSize, fontFamily, lineHeight, backgroundColor, textColor, isColorDark, columnCount]);

  // Public API methods via ref
  useEffect(() => {
    if (!hostRef.current) return;

    // Expose methods on the host element for external control
    (hostRef.current as any).shadowDOMAPI = {
      // Get shadow root (for advanced usage)
      getShadowRoot: () => shadowRootRef.current,

      // Add highlight to text
      addHighlight: (rangeInfo: any, className = 'highlight') => {
        if (!shadowRootRef.current) return null;

        // Implementation for adding highlights
        const range = document.createRange();
        // ... configure range from rangeInfo

        const highlight = document.createElement('span');
        highlight.className = className;

        try {
          range.surroundContents(highlight);
          return highlight;
        } catch (e) {
          console.error('Failed to add highlight:', e);
          return null;
        }
      },

      // Remove highlight
      removeHighlight: (highlightElement: HTMLElement) => {
        if (!highlightElement) return;

        const parent = highlightElement.parentNode;
        while (highlightElement.firstChild) {
          parent?.insertBefore(highlightElement.firstChild, highlightElement);
        }
        parent?.removeChild(highlightElement);
      },

      // Search text in content
      searchText: (searchTerm: string) => {
        if (!shadowRootRef.current) return [];

        const container = shadowRootRef.current.querySelector('.content-container');
        if (!container) return [];

        // Simple text search implementation
        const results: Range[] = [];
        const walker = document.createTreeWalker(
          container,
          NodeFilter.SHOW_TEXT,
          null
        );

        let node;
        while (node = walker.nextNode()) {
          const text = node.textContent || '';
          const index = text.toLowerCase().indexOf(searchTerm.toLowerCase());

          if (index !== -1) {
            const range = document.createRange();
            range.setStart(node, index);
            range.setEnd(node, index + searchTerm.length);
            results.push(range);
          }
        }

        return results;
      },

      // Update theme
      updateTheme: (newTheme: 'light' | 'dark') => {
        // Update theme-specific styles
        const selectionStyle = shadowRootRef.current?.querySelector('style');
        if (selectionStyle) {
          selectionStyle.textContent = selectionStyle.textContent?.replace(
            /::selection\s*{[^}]+}/,
            `::selection { background-color: ${newTheme === 'dark' ? 'rgb(var(--color-primary-500) / 0.5)' : 'rgb(var(--color-primary-500) / 0.3)'}; }`
          ) || '';
        }
      }
    };
  }, []);

  return (
    <div
      ref={hostRef}
      className={className}
      data-shadow-host="true"
    >
      {/* Fallback content for browsers without Shadow DOM support */}
      {!isReady && children}
    </div>
  );
});

ShadowDOMContentRenderer.displayName = 'ShadowDOMContentRenderer';

// Wrap in memo to prevent re-renders when parent re-renders (e.g., when modal opens)
// This prevents the Shadow DOM from being rebuilt and highlights from disappearing
export default React.memo(ShadowDOMContentRenderer);